import { Key } from "client/Misc/commonTypes";
import { isNil } from "client/Misc/utils";
import {
	useEffect,
	ReadonlyRefObject
} from "react";

export function useFocusTrap<T extends HTMLElement>(
	ref: ReadonlyRefObject<T | null>,
	isActive = true
): void {
	useEffect(
		() => {
			if (!isActive) {
				return;
			};

			const handleKeyDown = (e: KeyboardEvent): void => {
				if (e.key !== Key.Tab) {
					return;
				};

				const containerElement: T | null = ref.current;

				if (isNil(containerElement)) {
					return;
				}

				const focusableElements: readonly HTMLElement[] = getFocusableElements(containerElement);

				if (focusableElements.length === 0) {
					return;
				}

				const first: HTMLElement = focusableElements[0];
				const last: HTMLElement = focusableElements[focusableElements.length - 1];

				if (!containerElement.contains(document.activeElement)) {
					first.focus();
					return;
				}

				if (e.shiftKey && document.activeElement === first) {
					e.preventDefault();
					last.focus();
				} else if (!e.shiftKey && document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			};

			const cancellation = new AbortController();

			// Listen on document to catch keydown even when focus is not inside the container
			document.addEventListener(
				"keydown",
				handleKeyDown,
				{ signal: cancellation.signal }
			);

			return (): void => {
				cancellation.abort();
			};
		},
		[isActive, ref]
	);
}

const FOCUSABLE_SELECTORS = [
	`a[href]:not([tabindex="-1"])`,
	`button:not([disabled]):not([tabindex="-1"])`,
	`textarea:not([disabled]):not([tabindex="-1"])`,
	`input:not([disabled]):not([tabindex="-1"])`,
	`select:not([disabled]):not([tabindex="-1"])`,
	`[tabindex]:not([tabindex="-1"])`,
].join(",");

function getFocusableElements(container: HTMLElement): readonly HTMLElement[] {
	return Array.from(
		container.querySelectorAll(FOCUSABLE_SELECTORS)
	);
}